import { forkJoin, Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DocumentsService } from "../services/documents.service";
import { PurchaseOrder } from "../interfaces/purchase-order.interface";
import {
  NEXCAR_DOCUMENT_CATEGORY_CIRCULATION,
  NEXCAR_DOCUMENT_CATEGORY_ENDORSEMENT,
  NEXCAR_DOCUMENT_CATEGORY_INVOICE,
  NEXCAR_DOCUMENT_CATEGORY_REPORT,
  NEXCAR_EXPEDIENTE_RESOURCE_CLASS_PURCHASE_ORDER,
  NEXCAR_METADATA_FILTERS_PROBES,
  NEXCAR_UPLOAD_ORIGIN_METADATA_KEY,
  NEXCAR_UPLOAD_ORIGIN_NEXTCAR_DOCS,
} from "./nexcar-expediente-documents.constants";

/** Minimal document shape for trail checks. */
export interface NexcarTrailDocument {
  id?: number;
  status?: string;
  category?: string;
  metadata?: Record<string, unknown> | null;
}

export type NexcarExpedienteTrailMode = "docs_step_routing" | "operations_review_ui";

/**
 * Whether the PO has a Nexcar expediente record from API (not only legacy uploads).
 */
export function hasPurchaseOrderNexcarExpedientRecord(
  purchaseOrder: Pick<PurchaseOrder, "nexcar_expedient"> | null | undefined
): boolean {
  const e = purchaseOrder?.nexcar_expedient;
  if (!e) {
    return false;
  }
  if (typeof e.id === "number" && e.id > 0) {
    return true;
  }
  if (e.review_status && String(e.review_status).trim().length > 0) {
    return true;
  }
  if ((e.nexcar_document_uploads?.length ?? 0) > 0) {
    return true;
  }
  if (e.response_payload != null) {
    const p = e.response_payload as Record<string, unknown>;
    if (Object.keys(p).length > 0) {
      return true;
    }
  }
  if (e.webhook_payload != null) {
    const p = e.webhook_payload as Record<string, unknown>;
    if (Object.keys(p).length > 0) {
      return true;
    }
  }
  if (e.error_message && String(e.error_message).trim().length > 0) {
    return true;
  }
  return false;
}

export function normalizeDocsResponse(resp: unknown): NexcarTrailDocument[] {
  if (Array.isArray(resp)) {
    return resp as NexcarTrailDocument[];
  }
  if (!resp || typeof resp !== "object") {
    return [];
  }
  const asRecord = resp as Record<string, unknown>;
  const candidates: unknown[] = [
    asRecord["documents"],
    asRecord["documentos"],
    asRecord["items"],
    asRecord["data"],
    (asRecord["data"] as Record<string, unknown> | undefined)?.["documents"],
    (asRecord["data"] as Record<string, unknown> | undefined)?.["documentos"],
    (asRecord["data"] as Record<string, unknown> | undefined)?.["items"],
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as NexcarTrailDocument[];
    }
  }
  return [];
}

export function mergeNexcarTrailDocumentsUniqueById(...lists: NexcarTrailDocument[][]): NexcarTrailDocument[] {
  const byId = new Map<number, NexcarTrailDocument>();
  for (const list of lists) {
    for (const d of list) {
      const id = d.id;
      if (typeof id === "number") {
        byId.set(id, d);
      }
    }
  }
  return [...byId.values()];
}

export function hasNexcarWizardMarker(docs: ReadonlyArray<NexcarTrailDocument>): boolean {
  return docs.some((d) => {
    if (d.status === "erased") {
      return false;
    }
    const meta = d.metadata;
    if (meta == null || typeof meta !== "object") {
      return false;
    }
    const marker = (meta as Record<string, unknown>)[NEXCAR_UPLOAD_ORIGIN_METADATA_KEY];
    return marker === NEXCAR_UPLOAD_ORIGIN_NEXTCAR_DOCS;
  });
}

function hasNonErasedNexcarCategoryDocuments(docs: ReadonlyArray<NexcarTrailDocument>): boolean {
  const cat = (d: NexcarTrailDocument) => d.category ?? "";
  return docs.some(
    (d) =>
      d.status !== "erased" &&
      (cat(d) === NEXCAR_DOCUMENT_CATEGORY_INVOICE ||
        cat(d) === NEXCAR_DOCUMENT_CATEGORY_ENDORSEMENT ||
        cat(d) === NEXCAR_DOCUMENT_CATEGORY_CIRCULATION ||
        cat(d) === NEXCAR_DOCUMENT_CATEGORY_REPORT)
  );
}

/**
 * Resolves whether Nexcar-specific UI / step should apply for this PO.
 *
 * - **operations_review_ui**: Mesa — solo si hay expediente Nexcar o archivos subidos desde el wizard (`nexcar_upload_origin`).
 * - **docs_step_routing**: Cliente — además, si ya hay archivos en categorías de expediente sin ese marcador, se asume carga manual previa y no se fuerza el paso Nexcar; si no hay aún esos archivos, sí se ofrece Nexcar (flujo nuevo).
 */
export function observeNexcarExpedienteTrail(
  documentsService: DocumentsService,
  purchaseOrder: Pick<PurchaseOrder, "id" | "nexcar_expedient"> | null | undefined,
  mode: NexcarExpedienteTrailMode
): Observable<boolean> {
  if (!purchaseOrder?.id) {
    return of(false);
  }
  if (hasPurchaseOrderNexcarExpedientRecord(purchaseOrder)) {
    return of(true);
  }
  const poId = purchaseOrder.id;
  const categories = [...NEXCAR_METADATA_FILTERS_PROBES] as { [key: string]: { [key: string]: unknown } }[];
  return forkJoin({
    all: documentsService
      .getDocuments(String(poId), NEXCAR_EXPEDIENTE_RESOURCE_CLASS_PURCHASE_ORDER)
      .pipe(catchError(() => of([]))),
    byMeta: documentsService
      .getDocumentsByMetadata(poId, NEXCAR_EXPEDIENTE_RESOURCE_CLASS_PURCHASE_ORDER, categories)
      .pipe(catchError(() => of([]))),
  }).pipe(
    map(({ all, byMeta }) => {
      const merged = mergeNexcarTrailDocumentsUniqueById(
        normalizeDocsResponse(all),
        normalizeDocsResponse(byMeta)
      );
      if (hasNexcarWizardMarker(merged)) {
        return true;
      }
      if (mode === "operations_review_ui") {
        return false;
      }
      if (hasNonErasedNexcarCategoryDocuments(merged)) {
        return false;
      }
      return true;
    })
  );
}
